/**
 * @fileoverview Helper Content Data for Maintenance Checklists
 * @module helper-content
 * 
 * Business Context:
 * - Provides detailed educational content for each checklist item
 * - Helps Texas homeowners accurately identify and document home issues
 * - Content is tailored to support property tax appeals
 * 
 * Technical Context:
 * - Exports typed content data for use in HelperContent component
 * - Images reference paths in public/images/maintenance/
 */

// TypeScript interfaces
export interface HelperImage {
  src: string;
  alt: string;
  caption: string;
}

export interface HelperContentData {
  definition?: string;
  whyItMatters?: string;
  howToCheck?: string[];
  whatToLookFor?: string[];
  visualExamples?: HelperImage[];
  documentationTips?: string[];
  safetyWarning?: string;
  proTip?: string;
}

// Question text mapping for display in blade header
export const CHECKLIST_QUESTIONS: Record<string, string> = {
  // ROOF
  age_over_15: 'Is your roof over 15 years old?',
  visible_damage: 'Are there visible damaged, curling, or missing shingles?',
  hail_damage: 'Is there evidence of hail damage on the roof?',
  leaks_stains: 'Have you noticed water stains on ceilings or walls?',
  granule_loss: 'Are there granules in gutters or downspouts?',
  sagging_areas: 'Is there any visible sagging in the roof deck?',
  flashing_damage: 'Is flashing around vents, chimneys, or skylights damaged?',
  attic_ventilation: 'Is there inadequate attic ventilation or extreme attic heat?',
  wind_damage: 'Is there evidence of wind damage from storms?',
  professional_opinion_roof: 'Has a roofing professional recommended repairs or replacement?',
  
  // FOUNDATION
  visible_cracks: 'Are there visible cracks in the foundation or slab?',
  stair_step_cracks: 'Are there stair-step cracks in brick veneer?',
  horizontal_cracks: 'Are there horizontal cracks in the foundation?',
  doors_windows_stick: 'Do doors or windows stick, not close, or show gaps?',
  uneven_floors: 'Are there uneven or sloping floors?',
  gaps_walls_ceiling: 'Are there gaps between walls and ceiling/floor?',
  soil_separation: 'Is there visible separation between soil and foundation?',
  pier_beam_issues: 'If pier & beam, are there sagging floors or damaged piers?',
  previous_repairs: 'Are there signs of previous foundation repairs (piers, mudjacking)?',
  professional_inspection: 'Has a structural engineer recommended repairs?',
  
  // HVAC
  age_over_10: 'Is your HVAC system over 10 years old?',
  uneven_cooling: "Are there rooms that don't cool properly during Texas summers?",
  high_utility_bills: 'Have your electric bills increased significantly?',
  struggles_over_100: 'Does your AC struggle when temperatures exceed 100°F?',
  strange_noises: 'Does the system make unusual noises?',
  poor_airflow: 'Is there weak airflow from vents?',
  frequent_cycling: 'Does the system turn on and off frequently?',
  humidity_issues: 'Are there humidity problems (too humid or too dry)?',
  ductwork_issues: 'Is there damage or disconnection in attic ductwork?',
  professional_opinion_hvac: 'Has an HVAC professional recommended repairs or replacement?',
  
  // PLUMBING
  pipe_age: 'Are the pipes in your home original or over 30 years old?',
  low_water_pressure: 'Is there low water pressure throughout the home?',
  slow_drains: 'Are there slow drains in multiple fixtures?',
  water_discoloration: 'Is there discolored water from faucets?',
  visible_leaks: 'Are there visible leaks under sinks or around fixtures?',
  water_heater_issues: 'Is the water heater over 8-10 years old or having issues?',
  slab_leak: 'Is there evidence of a slab leak?',
  sewer_issues: 'Are there sewer odors or backup issues?',
  polybutylene_pipes: 'Does your home have polybutylene (gray) pipes?',
  freeze_damage: 'Was there pipe damage from a freeze event?',
  professional_opinion_plumbing: 'Has a plumber recommended repairs or repiping?',
  
  // ELECTRICAL
  panel_age: 'Is the electrical panel over 25 years old?',
  fuse_box: 'Does the home still have a fuse box instead of breakers?',
  panel_brand: 'Is it a Federal Pacific, Zinsco, or Pushmatic panel?',
  frequent_trips: 'Do circuit breakers trip frequently, especially in summer?',
  flickering_lights: 'Are there flickering or dimming lights when AC runs?',
  two_prong_outlets: 'Are there ungrounded (2-prong) outlets?',
  warm_outlets: 'Do outlets or switches feel warm to touch?',
  aluminum_wiring: 'Does the home have aluminum wiring?',
  insufficient_capacity: 'Is the service undersized (100 amp or less)?',
  professional_opinion_electrical: 'Has an electrician recommended panel upgrade or rewiring?',
};

// Helper content data for all checklist items
const HELPER_CONTENT: Record<string, HelperContentData> = {
  // ============================================
  // ROOF CHECKLIST ITEMS
  // ============================================
  
  age_over_15: {
    definition: "Roof age is measured from the original installation or last full replacement. In Texas, composition (asphalt) shingles typically have a functional lifespan of 15-20 years due to our intense heat and UV exposure, even if manufacturer warranties suggest longer.",
    
    whyItMatters: "Texas heat and UV radiation significantly accelerate roof aging compared to northern states. A 15-year-old roof in Texas has experienced the equivalent wear of a 20-25 year old roof elsewhere. This shortened lifespan represents real depreciation that should be reflected in your property assessment.",
    
    howToCheck: [
      "Check your records for the original roof installation date",
      "Look at your home purchase inspection report",
      "Ask previous owners or check home warranty documents",
      "A roofer can sometimes estimate age by shingle condition"
    ],
    
    whatToLookFor: [
      "Original installation receipts or warranty documents",
      "Home inspection reports from purchase",
      "Building permits (available at county)",
      "Insurance claim records if replaced after storm"
    ],
    
    documentationTips: [
      "Gather any receipts or contracts from roof installation",
      "Request permit records from your county if available",
      "Note the approximate installation year even without documents",
      "Photos showing overall condition support age-related wear claims"
    ],
    
    proTip: "Even without exact documentation, appraisal districts understand that most roofs in Texas homes over 20 years old have likely been replaced at least once. The key is demonstrating current condition, not proving exact age."
  },

  visible_damage: {
    definition: "Visible roof damage includes shingles that are curling (edges lifting up), cracking, buckling, or completely missing. This damage exposes the underlying roof deck to water intrusion and accelerates deterioration of surrounding shingles.",
    
    whyItMatters: "Texas experiences extreme temperature swings - from freezing winters to 100°F+ summers. This thermal cycling causes shingles to expand and contract repeatedly, leading to curling and cracking. Visible damage indicates your roof is actively deteriorating and losing its protective capability.",
    
    howToCheck: [
      "Walk around your home and look at the roof from ground level",
      "Use binoculars for a closer view without climbing",
      "Check all roof slopes, especially south and west facing",
      "Look after storms when damage is most visible",
      "Compare your roof to neighbors with newer roofs"
    ],
    
    whatToLookFor: [
      "Shingles with edges curling upward or cupping",
      "Cracked or split shingles",
      "Missing shingles leaving exposed underlayment",
      "Shingles that appear wavy or buckled",
      "Dark patches where granules have worn off"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/shingle-curling.jpg", 
        alt: "Curling shingles on roof", 
        caption: "Curled shingle edges indicate thermal damage and aging - common after 10-15 years in Texas" 
      },
      { 
        src: "/images/maintenance/shingle-cracking.jpg", 
        alt: "Cracked roof shingles", 
        caption: "Cracking allows water infiltration and indicates the shingles have lost flexibility" 
      }
    ],
    
    documentationTips: [
      "Take wide shots showing overall roof condition",
      "Get close-ups of specific damaged areas",
      "Photograph from multiple angles if possible",
      "Include something for scale in close-up photos",
      "Note the direction each roof slope faces"
    ],
    
    proTip: "South and west-facing roof slopes receive the most sun exposure in Texas and typically show damage first. Point this out specifically in your appeal - it demonstrates you understand why damage occurs."
  },

  hail_damage: {
    definition: "Hail damage appears differently depending on your roofing material. On asphalt shingles, it looks like dark spots or bruises where the granules have been knocked off. On metal roofs, you'll see dents. On tile, you'll find cracks or chips.",
    
    whyItMatters: "Texas is in 'Hail Alley' - the region with the most frequent damaging hailstorms in the United States. Hail damage shortens roof life significantly because it compromises the protective granule layer on shingles, exposing the asphalt to UV degradation.",
    
    howToCheck: [
      "Look for random pattern of dark spots on shingles",
      "Check metal vents, gutters, and AC units for dent marks",
      "Examine soft metals like roof vents - they show dents easily",
      "Review weather history for your area after storms",
      "Have a roofer inspect after major hail events"
    ],
    
    whatToLookFor: [
      "Dark spots on shingles (where granules are gone)",
      "Soft, bruised feeling when pressing on shingle (requires ladder)",
      "Dents in gutters, downspouts, or metal flashing",
      "Dents in AC unit or other outdoor equipment",
      "Cracked or chipped clay/concrete tiles"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/hail-damage-shingle.jpg", 
        alt: "Hail damage on asphalt shingle", 
        caption: "Hail impacts create dark spots where granules have been knocked off - these spots will deteriorate rapidly" 
      },
      { 
        src: "/images/maintenance/hail-damage-metal.jpg", 
        alt: "Hail dents on metal surface", 
        caption: "Check metal vents and gutters for dents - they're easier to see than shingle damage" 
      }
    ],
    
    documentationTips: [
      "Photograph any dented gutters, vents, or AC units",
      "Document the date of recent hail storms in your area",
      "Keep insurance adjuster reports if available",
      "Note any repairs already made to storm damage",
      "Get a roofer's inspection report specifically mentioning hail"
    ],
    
    safetyWarning: "Do not climb on a wet or damaged roof to inspect for hail damage. Use binoculars from the ground or hire a professional inspector.",
    
    proTip: "Check your outdoor AC condenser unit - the thin aluminum fins dent easily in hail storms. Dented AC fins are strong evidence that hail large enough to damage your roof hit your property."
  },

  leaks_stains: {
    definition: "Water stains on ceilings or walls indicate that water has penetrated your roof or is leaking from plumbing. Roof-related stains typically appear as brown or yellow discolored areas, often with rings showing repeated wetting.",
    
    whyItMatters: "Active or past leaks indicate significant roof problems. Even 'old' stains suggest the roof has failed at some point. In Texas, heavy thunderstorms can overwhelm compromised roofing, and leaks often lead to additional hidden damage in attics and walls.",
    
    howToCheck: [
      "Look at ceilings in all rooms, especially top floor",
      "Check around skylights, vents, and chimney areas",
      "Look at walls near roof edges and corners",
      "Check in closets and utility rooms often missed in daily life",
      "Use a flashlight in the attic during/after rain"
    ],
    
    whatToLookFor: [
      "Brown or yellow discolored areas on ceilings",
      "Ring patterns showing repeated water exposure",
      "Bubbling or peeling paint",
      "Soft or sagging drywall",
      "Mold or mildew growth"
    ],
    
    documentationTips: [
      "Photograph all stained areas with good lighting",
      "Measure the size of stained areas",
      "Note when stains appeared or grew",
      "Document any mold presence carefully",
      "Keep records of any leak repairs"
    ],
    
    proTip: "Look in your attic with a flashlight during a rainstorm if you can safely access it. Active drips or wet spots on rafters pinpoint exactly where your roof is failing - powerful evidence for your appeal."
  },

  granule_loss: {
    definition: "Granules are the small, sand-like particles that coat asphalt shingles. They protect the underlying asphalt from UV rays and provide fire resistance. When granules wash off, the shingle's asphalt layer is exposed and deteriorates rapidly.",
    
    whyItMatters: "In Texas, intense UV exposure accelerates granule loss significantly. A roof losing granules in Texas may have only 5-7 years of life remaining, even if it looks okay from the ground. This represents real depreciation in your home's value.",
    
    howToCheck: [
      "Look in your gutters and downspouts after a rain",
      "Check where downspouts discharge onto the ground",
      "Look for bare or shiny spots on shingles (binoculars help)",
      "Check gutters at multiple locations around the house",
      "Note: New roofs shed some granules initially - normal for first year"
    ],
    
    whatToLookFor: [
      "Accumulation of sand-like particles in gutters",
      "Granules piled at downspout discharge points",
      "Dark or bare patches visible on shingles",
      "Shingles that look shiny instead of matte",
      "Streaks of washed-off granules on lower shingles"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/granules-in-gutter.jpg", 
        alt: "Granules accumulated in gutter", 
        caption: "Granules collect in gutters and look like coarse, colored sand - significant accumulation indicates advanced wear" 
      }
    ],
    
    documentationTips: [
      "Photograph granules in gutters with something for scale (coin, ruler)",
      "Document any bare patches visible on roof",
      "Note approximate age of roof if known",
      "Photograph granule piles at downspout discharge areas",
      "Scoop some granules into a bag as physical evidence"
    ],
    
    proTip: "If you can safely access your gutters, scoop out a handful of granules to show the appraisal board. The visual impact of actual granules can be powerful evidence. A sandwich bag full of granules says more than any photo."
  },

  sagging_areas: {
    definition: "Roof sagging appears as a dip or wave in what should be a straight roof line. It indicates that the roof deck (the plywood under your shingles) or the supporting rafters/trusses have weakened, often from water damage, age, or inadequate support.",
    
    whyItMatters: "A sagging roof is a serious structural issue that significantly impacts home value. In Texas, prolonged heat can weaken roof decking over time, and water intrusion from storms accelerates decay. Sagging roofs often require complete replacement, not just new shingles.",
    
    howToCheck: [
      "Stand across the street and look at your roof lines",
      "Look for dips or waves in the ridge line (peak of roof)",
      "Check the edges (eaves) for any drooping",
      "Compare your roof lines to neighbors' homes",
      "Look at the roof from multiple angles"
    ],
    
    whatToLookFor: [
      "Visible dip or bow in the ridge line",
      "Waviness along what should be straight lines",
      "One section sitting lower than others",
      "Sagging between rafters (wavy appearance)",
      "Any visible deflection from what should be flat planes"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/roof-sagging.jpg", 
        alt: "Sagging roof line", 
        caption: "The visible dip in this roof line indicates structural problems in the decking or rafters beneath" 
      }
    ],
    
    documentationTips: [
      "Photograph roof from street level showing the sag",
      "Take multiple angles to clearly show the issue",
      "Use a straight edge in the photo for reference",
      "Document when you first noticed the sagging",
      "Get a professional structural assessment"
    ],
    
    safetyWarning: "Do not walk on a sagging roof. The underlying structure is compromised and may not support weight safely. Hire a professional to inspect.",
    
    proTip: "Take photos from across the street or from a neighbor's yard. Distance shots often show roof line problems more clearly than close-ups because you can see the full scope of the deflection."
  },

  flashing_damage: {
    definition: "Flashing is the thin metal (usually aluminum or galvanized steel) installed where your roof meets walls, chimneys, vents, or skylights. It channels water away from these vulnerable joints. Damaged or deteriorated flashing is one of the most common causes of roof leaks.",
    
    whyItMatters: "Texas temperature swings cause metal flashing to expand and contract constantly, which can loosen seals and create gaps. Our severe thunderstorms then drive water into these gaps. Flashing failure is often the first point of water intrusion in Texas roofs.",
    
    howToCheck: [
      "Look at where the roof meets walls or chimneys",
      "Check around all roof vents and pipes",
      "Examine skylight edges if you have them",
      "Look for lifted, bent, or rusty metal",
      "Check for tar or caulk patches (signs of previous repairs)"
    ],
    
    whatToLookFor: [
      "Visible rust on metal flashing",
      "Lifted or bent sections of flashing",
      "Gaps between flashing and roof/wall",
      "Missing sections of flashing",
      "Excessive caulk or tar (indicates patching)",
      "Stains on walls below flashing areas"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/flashing-types.jpg", 
        alt: "Types of roof flashing locations", 
        caption: "Flashing is found where roofs meet walls, chimneys, and around vents - these are critical water barriers" 
      },
      { 
        src: "/images/maintenance/flashing-damaged.jpg", 
        alt: "Damaged roof flashing", 
        caption: "Lifted, rusted, or gap-filled flashing allows water behind the roof surface" 
      }
    ],
    
    documentationTips: [
      "Photograph each flashing location on your roof",
      "Get close-ups of visible rust, gaps, or lifting",
      "Document any water stains below flashing areas",
      "Note any past repairs to flashing",
      "Include photos of caulk/tar patches as evidence of problems"
    ],
    
    proTip: "If you see black tar or lots of caulk around flashing, that's evidence of past leaks and temporary repairs. This shows ongoing problems - appraisal boards understand that patched flashing indicates deferred maintenance."
  },

  attic_ventilation: {
    definition: "Proper attic ventilation allows hot air to escape through ridge vents or roof vents while drawing cooler air in through soffit vents at the eaves. Without adequate ventilation, attics in Texas can reach 150-160°F, dramatically shortening roof and shingle life.",
    
    whyItMatters: "Texas summer heat makes attic ventilation critical. An inadequately ventilated attic cooks your roof from below while the sun cooks it from above. This double heat exposure can cut shingle life in half and dramatically increase cooling costs.",
    
    howToCheck: [
      "Look for vents at the roof peak (ridge vents) or on the roof surface",
      "Check under eaves for soffit vents",
      "If safely accessible, check attic temperature on a hot day",
      "Look for exhaust fans or powered ventilators",
      "Count the number of vents relative to attic size"
    ],
    
    whatToLookFor: [
      "Absence of visible roof vents",
      "Blocked or painted-over soffit vents",
      "Extreme heat when entering attic (even briefly)",
      "Warped or damaged stored items in attic",
      "Visible heat damage to rafters or decking"
    ],
    
    documentationTips: [
      "Photograph your roof vents (or lack thereof)",
      "Document soffit vent condition",
      "Note the size of your attic space vs. ventilation",
      "If safe, record attic temperature on a hot summer day",
      "Get an energy audit that addresses ventilation"
    ],
    
    safetyWarning: "Texas attics can reach dangerous temperatures in summer. Never enter an attic during the hottest part of the day. If you must check, go early morning and limit time to a few minutes. Heat stroke is a real risk.",
    
    proTip: "If your attic is unbearably hot even early in the morning during summer, take a temperature reading with a thermometer you can photograph. Documented extreme temps (140°F+) are compelling evidence of ventilation problems."
  },

  wind_damage: {
    definition: "Wind damage occurs when strong gusts lift, crack, or tear shingles from your roof. Texas thunderstorms, hurricanes, and tornadoes can produce winds exceeding 60-100+ mph. Even moderate wind damage creates entry points for water.",
    
    whyItMatters: "Texas experiences some of the most severe thunderstorms in the country, with damaging straight-line winds even when no tornado touches down. Wind damage often leads to progressive failure - once shingles lift, more are likely to follow.",
    
    howToCheck: [
      "Look for lifted or creased shingles",
      "Check for shingles on the ground after storms",
      "Examine areas near roof edges (most vulnerable)",
      "Look at spots where wind hits directly",
      "Compare to neighbors for storm-related patterns"
    ],
    
    whatToLookFor: [
      "Lifted shingle corners or edges",
      "Creased or folded-over shingles",
      "Missing shingles or exposed underlayment",
      "Debris impact damage",
      "Damaged ridge caps (along the peak)"
    ],
    
    documentationTips: [
      "Photograph lifted or missing shingles",
      "Document dates of recent severe storms",
      "Keep any insurance communications about storm damage",
      "Note if neighbors had similar damage",
      "Save weather alerts or storm reports for your area"
    ],
    
    proTip: "After major storms, check the National Weather Service records for wind speeds reported in your area. Documented high winds on a specific date combined with photos of damage tell a compelling story."
  },

  professional_opinion_roof: {
    definition: "A professional roofing assessment is an evaluation by a licensed roofer or inspector who documents the condition of your roof and recommends repairs or replacement. These assessments carry weight because they come from qualified experts.",
    
    whyItMatters: "Professional documentation strengthens your appeal significantly. Appraisal districts give considerable weight to licensed contractor opinions because they're backed by expertise and professional liability. A written assessment is much stronger than homeowner observations alone.",
    
    howToCheck: [
      "Contact 2-3 local roofing companies for assessments",
      "Ask for written inspection reports, not just verbal quotes",
      "Request they document specific issues found",
      "Ask if they'll provide photos with their report",
      "Get estimates for recommended repairs"
    ],
    
    whatToLookFor: [
      "Written report on company letterhead",
      "Specific issues identified with locations",
      "Recommendations (repair vs. replacement)",
      "Cost estimates for recommended work",
      "Photos taken during inspection"
    ],
    
    documentationTips: [
      "Get at least one detailed written assessment",
      "Request the inspector include photos",
      "Ask for their license number on the report",
      "Keep all estimates and proposals",
      "Date-stamp when assessments were performed"
    ],
    
    proTip: "Many roofing companies offer free inspections hoping to earn your business. Take advantage of this - get 2-3 assessments. If they all recommend replacement, that's powerful consensus evidence for your appeal."
  },

  // ============================================
  // FOUNDATION CHECKLIST ITEMS
  // ============================================

  visible_cracks: {
    definition: "Foundation cracks are fractures in your concrete slab or foundation walls. They range from hairline (less than 1/16 inch) to significant structural cracks (1/4 inch or wider). The pattern, direction, and width of cracks indicate different types and severities of foundation movement.",
    
    whyItMatters: "Texas sits on expansive clay soils that swell dramatically when wet and shrink when dry. This constant movement is the #1 cause of foundation problems in the state. Foundation issues directly impact home value and are one of the strongest factors in property tax appeals.",
    
    howToCheck: [
      "Walk the exterior perimeter of your home",
      "Check where foundation meets brick or siding",
      "Look at interior corners and near doors/windows",
      "Check garage floor and walls",
      "Look for cracks in concrete driveways near the house"
    ],
    
    whatToLookFor: [
      "Vertical cracks (often from settlement)",
      "Horizontal cracks (serious - may indicate pressure)",
      "Stair-step cracks in brick",
      "Cracks wider at top than bottom (or vice versa)",
      "Cracks with displacement (one side higher than other)"
    ],
    
    documentationTips: [
      "Photograph all cracks with a ruler for scale",
      "Measure and note the width of significant cracks",
      "Document the location of each crack",
      "Take photos over time to show if cracks are growing",
      "Note any repairs previously attempted"
    ],
    
    proTip: "Place painter's tape across cracks with the date written on it. If the tape tears or gaps widen over weeks/months, photograph this - it proves the foundation is actively moving."
  },

  stair_step_cracks: {
    definition: "Stair-step cracks follow the mortar joints between bricks in a diagonal, step-like pattern. They're one of the most recognizable signs of foundation movement and indicate that the foundation underneath has shifted, causing the brick veneer to crack along its weakest points.",
    
    whyItMatters: "Stair-step cracks are extremely common in Texas homes due to our expansive clay soils. They're highly visible and directly demonstrate that foundation movement has occurred. Insurance companies, buyers, and appraisers all recognize these as significant defects.",
    
    howToCheck: [
      "Look at exterior brick walls, especially near corners",
      "Check near windows and doors where stress concentrates",
      "Examine the full height of brick from ground to roofline",
      "Look at both sides of the house (one side often worse)",
      "Check brick around garage doors"
    ],
    
    whatToLookFor: [
      "Diagonal cracks following mortar joints in steps",
      "Cracks starting from corners of windows/doors",
      "Separated mortar joints",
      "Cracks wider at one end than the other",
      "Multiple parallel stair-step cracks"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/stair-step-cracks.jpg", 
        alt: "Stair-step cracks in brick", 
        caption: "Classic stair-step pattern follows the mortar joints - indicates foundation has shifted" 
      }
    ],
    
    documentationTips: [
      "Photograph stair-step cracks from a distance to show the pattern",
      "Get close-ups showing crack width",
      "Measure the widest point of each crack",
      "Document all locations around the house",
      "Note if cracks appear on multiple walls"
    ],
    
    proTip: "Stair-step cracks that run in opposite directions on different walls indicate the foundation has both heaved (risen) and settled in different areas. Document all walls to show the full scope of movement."
  },

  horizontal_cracks: {
    definition: "Horizontal cracks run sideways across foundation walls and are often the most serious type of crack. They typically indicate lateral pressure from soil pushing against the foundation, which can threaten structural integrity.",
    
    whyItMatters: "Horizontal cracks are a red flag for foundation stability. In Texas, they often result from hydrostatic pressure when clay soils absorb water and expand against the foundation wall. These cracks may indicate the foundation is being pushed inward.",
    
    howToCheck: [
      "Examine foundation walls in basement or crawl space",
      "Check the exposed foundation around your home's perimeter",
      "Look for cracks running parallel to the ground",
      "Check both interior and exterior foundation surfaces",
      "Look for any bowing or bulging in foundation walls"
    ],
    
    whatToLookFor: [
      "Cracks running horizontally across the foundation",
      "Inward bowing of the wall (use a straight edge)",
      "Water seepage along horizontal cracks",
      "Cracks that are wider in the middle than at ends",
      "Displacement (one side of crack pushed in)"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/horizontal-crack.jpg", 
        alt: "Horizontal crack in foundation", 
        caption: "Horizontal cracks indicate pressure against the foundation wall - a serious structural concern" 
      }
    ],
    
    documentationTips: [
      "Photograph the full length of horizontal cracks",
      "Measure crack width at multiple points",
      "Document any wall bowing with photos",
      "Note any water seepage or staining",
      "Get a structural engineer's assessment"
    ],
    
    safetyWarning: "Significant horizontal cracking with wall movement is a serious structural issue. Have a structural engineer evaluate before making any decisions about repair or your appeal.",
    
    proTip: "Use a long straightedge or string line against the wall and photograph any gaps showing the wall has bowed inward. Visual proof of bowing is compelling evidence of structural problems."
  },

  doors_windows_stick: {
    definition: "Doors and windows that stick, won't close properly, or show gaps around frames are classic indicators of foundation movement. When the foundation shifts, it warps door and window frames, causing them to bind or leave gaps.",
    
    whyItMatters: "This is one of the most noticeable daily impacts of foundation problems. In Texas, doors sticking during wet seasons and then gapping during dry seasons indicates your foundation is responding to soil moisture changes - a chronic issue that affects livability.",
    
    howToCheck: [
      "Test every interior and exterior door",
      "Check all windows for smooth operation",
      "Look for gaps between frames and walls",
      "Notice if problems change with seasons",
      "Check door latches for alignment"
    ],
    
    whatToLookFor: [
      "Doors that stick or won't close without force",
      "Gaps between door/window and frame",
      "Visible daylight around exterior doors",
      "Latches that don't align with strike plates",
      "Windows that won't slide or lock properly"
    ],
    
    documentationTips: [
      "Video doors that won't close properly",
      "Photograph gaps with a ruler for scale",
      "Document which doors/windows are affected",
      "Note if problems are seasonal",
      "Show misaligned latches and strike plates"
    ],
    
    proTip: "Video yourself trying to close a sticking door - the struggle is clear evidence. Also photograph any gaps showing daylight around exterior doors - this directly shows energy loss and security concerns."
  },

  uneven_floors: {
    definition: "Floor slope indicates differential foundation settlement - where one part of your foundation has moved more than another. In Texas, this is typically caused by expansive clay soils that swell when wet and shrink when dry, causing different parts of the slab to rise and fall.",
    
    whyItMatters: "Uneven floors are one of the most visible and measurable signs of foundation issues, which are extremely common in Texas. Significant floor slope directly impacts livability and resale value, making it a strong factor for your appeal.",
    
    howToCheck: [
      "Place a level on the floor in multiple locations",
      "Use a marble or golf ball - place it on hard floors and watch if it rolls",
      "Walk through your home and note where floors feel uneven",
      "Check near exterior walls and in center of rooms",
      "Measure from floor to ceiling in multiple spots"
    ],
    
    whatToLookFor: [
      "Marble rolls consistently in one direction",
      "Level shows significant tilt (more than 1 inch in 20 feet is concerning)",
      "Floors feel 'bouncy' in certain areas",
      "Visible gaps between floor and baseboards",
      "Doors that swing open or closed on their own"
    ],
    
    documentationTips: [
      "Video a marble rolling across the floor",
      "Photograph a level showing the tilt",
      "Measure and note the slope (inches over distance)",
      "Document which rooms/areas are affected",
      "Use a laser level for precise measurements if available"
    ],
    
    proTip: "The marble test is simple but effective evidence. Place the marble on a hard floor, let go, and video it rolling. Appraisal boards understand this visual immediately - it's something anyone can relate to."
  },

  gaps_walls_ceiling: {
    definition: "Gaps between walls and ceilings or floors indicate structural separation caused by foundation movement. When the foundation shifts, walls may pull away from ceilings or floors, creating visible gaps along these joints.",
    
    whyItMatters: "These gaps are unmistakable visual evidence that your home's structure has moved. They also create energy efficiency problems and can allow pest entry. In Texas, these gaps often appear during drought conditions and may partially close during wet periods.",
    
    howToCheck: [
      "Look where walls meet ceilings throughout your home",
      "Check where walls meet floors at baseboards",
      "Examine corners where two walls meet",
      "Look at both interior and exterior wall connections",
      "Check near windows and doors"
    ],
    
    whatToLookFor: [
      "Visible gaps at wall-ceiling joints",
      "Separated baseboards from walls",
      "Gaps at corners where walls meet",
      "Cracked or separated crown molding",
      "Gaps that change size seasonally"
    ],
    
    documentationTips: [
      "Photograph all gaps with a ruler for scale",
      "Document the locations throughout the house",
      "Note if gaps change with seasons",
      "Measure the widest point of each gap",
      "Take multiple photos over time to show changes"
    ],
    
    proTip: "If you notice gaps growing during dry months and shrinking during wet months, document this pattern with photos on specific dates. This proves the foundation is actively responding to soil conditions."
  },

  soil_separation: {
    definition: "Soil separation is the visible gap that forms between the soil and your foundation during drought conditions. As Texas clay soils dry out, they shrink dramatically, pulling away from the foundation and leaving a visible gap.",
    
    whyItMatters: "This gap is a clear indicator that you have expansive clay soils - the #1 cause of foundation problems in Texas. When rains return, water pours directly into this gap and against your foundation, accelerating damage. The gap itself also allows foundation movement.",
    
    howToCheck: [
      "Walk the perimeter of your home, especially during dry weather",
      "Look where soil meets the foundation",
      "Check after several weeks without rain",
      "Look for the gap to increase during summer drought",
      "Check multiple sides of the house"
    ],
    
    whatToLookFor: [
      "Visible gap between soil and foundation",
      "Gap typically 1-4 inches wide during drought",
      "Cracked soil surface near the foundation",
      "Gap that closes during rainy periods",
      "Deeper gaps in some areas than others"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/soil-separation.jpg", 
        alt: "Gap between soil and foundation", 
        caption: "This gap forms when clay soil shrinks during Texas droughts - it allows water to flow directly against your foundation" 
      }
    ],
    
    documentationTips: [
      "Photograph the gap with a ruler showing width",
      "Document during peak dry season",
      "Show the gap around all sides of the house",
      "Note that this is evidence of expansive clay soil",
      "Document if you've tried to maintain consistent moisture"
    ],
    
    proTip: "Photograph the same spot during drought (showing gap) and after rain (gap closed). This before/after comparison powerfully demonstrates the soil movement affecting your foundation."
  },

  pier_beam_issues: {
    definition: "Pier and beam foundations support the house on concrete piers (vertical posts) and wooden beams, with a crawl space underneath. Issues include rotting beams, shifting piers, inadequate support, and moisture damage in the crawl space.",
    
    whyItMatters: "Many older Texas homes have pier and beam foundations, which require different maintenance than slabs. These foundations are susceptible to wood rot from moisture, shifting from soil movement, and pest damage. Problems are often hidden in the crawl space.",
    
    howToCheck: [
      "Access the crawl space if possible (or hire an inspector)",
      "Look for sagging or bouncy floors from inside the house",
      "Check for moisture, musty smells, or visible wood rot",
      "Look for pest damage or evidence",
      "Feel for soft spots in flooring"
    ],
    
    whatToLookFor: [
      "Sagging floors or noticeable dips",
      "Bouncy or springy floors when walking",
      "Rotting or water-damaged beams",
      "Piers that have shifted or settled",
      "Evidence of termites or wood-boring insects"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/pier-beam-crawlspace.jpg", 
        alt: "Pier and beam foundation crawl space", 
        caption: "Pier and beam foundations have piers supporting wooden beams - look for rot, shifting, or damage" 
      }
    ],
    
    documentationTips: [
      "If safe, photograph crawl space conditions",
      "Document any visible rot or damage",
      "Note areas where floors sag or bounce",
      "Get a professional pier and beam inspection",
      "Document any moisture issues"
    ],
    
    safetyWarning: "Crawl spaces may have low oxygen, pests, or structural hazards. Consider hiring a professional inspector rather than entering yourself. Never enter a crawl space alone.",
    
    proTip: "Pier and beam foundations can often be adjusted and repaired more easily than slabs, but this still costs thousands of dollars. Get contractor estimates to document the financial impact."
  },

  previous_repairs: {
    definition: "Signs of previous foundation repairs include concrete pier patches, evidence of mudjacking (holes in concrete filled with material), steel piers visible at foundation edge, or any documentation of past foundation work.",
    
    whyItMatters: "Previous repairs indicate your home has already experienced foundation problems significant enough to require professional intervention. Even if repaired, this history affects home value and shows the property sits on problematic soil.",
    
    howToCheck: [
      "Look for circular patches in concrete (mudjacking holes)",
      "Check foundation edge for visible steel pier brackets",
      "Look for newer concrete sections around foundation",
      "Review home records for repair invoices",
      "Check disclosure documents if you purchased recently"
    ],
    
    whatToLookFor: [
      "Circular patches 2-3 inches diameter in concrete",
      "Steel brackets visible at foundation edge",
      "Sections of newer/different concrete",
      "Warranties or invoices from foundation companies",
      "Disclosure of repairs in purchase documents"
    ],
    
    documentationTips: [
      "Photograph any visible repair evidence",
      "Gather all repair documentation you have",
      "Note names of companies that did repairs",
      "Check if repairs have transferable warranties",
      "Document any recurring problems after repairs"
    ],
    
    proTip: "Even if you didn't have repairs done, check with neighbors. If multiple homes on your street have had foundation work, it demonstrates the entire area has problem soils - helpful context for your appeal."
  },

  professional_inspection: {
    definition: "A professional foundation inspection by a structural engineer or licensed foundation specialist provides an expert assessment of your foundation's condition, identifies problems, and recommends repairs with cost estimates.",
    
    whyItMatters: "In Texas, foundation issues are so common that a professional assessment carries significant weight with appraisal boards. Engineers can identify problems not visible to homeowners and provide repair cost estimates that directly support value adjustments.",
    
    howToCheck: [
      "Contact a structural engineer for inspection",
      "Get quotes from foundation repair companies",
      "Ask for detailed written reports",
      "Request cost estimates for recommended repairs",
      "Get multiple opinions for significant issues"
    ],
    
    whatToLookFor: [
      "Written report on letterhead",
      "Specific measurements of foundation level",
      "Identification of problem areas",
      "Recommended repairs with costs",
      "Engineer's license number and seal"
    ],
    
    documentationTips: [
      "Get at least one professional inspection report",
      "Request detailed findings with photos",
      "Keep all repair estimates",
      "Note engineer's credentials",
      "Document any urgency indicated in the report"
    ],
    
    proTip: "Many foundation companies offer free inspections hoping to win repair work. Get 2-3 assessments. If all recommend the same repairs, you have powerful consensus from professionals."
  },

  // ============================================
  // HVAC CHECKLIST ITEMS
  // ============================================

  age_over_10: {
    definition: "HVAC system age is measured from original installation. In Texas, central AC systems typically have a functional lifespan of 10-15 years due to our extreme summer heat. Systems work much harder here than in milder climates, leading to faster wear.",
    
    whyItMatters: "Texas AC systems run 6-8 months per year, often cycling constantly during 100°F+ summers. A 10-year-old system in Texas has accumulated wear equivalent to a 15-20 year old system in cooler states. This represents significant depreciation.",
    
    howToCheck: [
      "Look for a data plate on your outdoor unit",
      "Check the furnace or air handler for installation date",
      "Review home purchase inspection reports",
      "Check for installation receipts or warranty documents",
      "HVAC companies can determine age from serial numbers"
    ],
    
    whatToLookFor: [
      "Manufacturing date on equipment data plate",
      "Serial number that encodes manufacture date",
      "Original installation documentation",
      "Warranty cards with purchase date",
      "SEER rating (lower ratings indicate older systems)"
    ],
    
    documentationTips: [
      "Photograph the data plate on your equipment",
      "Record make, model, and serial number",
      "Document SEER rating if visible",
      "Note any original installation documents",
      "Have HVAC company decode age from serial"
    ],
    
    proTip: "If your system has a SEER rating of 10 or less, it's definitely old - minimum standards have increased over time. Low SEER ratings also mean high operating costs, another depreciation factor."
  },

  uneven_cooling: {
    definition: "Uneven cooling means some rooms or areas of your home don't reach the set temperature while others do. This indicates the HVAC system is undersized, ductwork is inadequate, insulation is poor, or the system is struggling to meet demand.",
    
    whyItMatters: "In Texas summers, an HVAC system that can't cool the entire home represents a significant quality-of-life and value issue. Uneven cooling often indicates underlying system problems that require repair or replacement to fix properly.",
    
    howToCheck: [
      "Set your thermostat and wait for the system to cycle",
      "Use a thermometer in different rooms",
      "Check rooms farthest from the air handler",
      "Note rooms that are consistently warmer",
      "Feel airflow at vents throughout the house"
    ],
    
    whatToLookFor: [
      "Temperature differences of 5°F+ between rooms",
      "Rooms that never reach thermostat setting",
      "Weak airflow from certain vents",
      "System running constantly without cooling some rooms",
      "Upper floors significantly hotter than lower"
    ],
    
    documentationTips: [
      "Record temperatures in different rooms",
      "Note which rooms are consistently problematic",
      "Document if the issue is worse during peak heat",
      "Get an HVAC assessment of system capacity",
      "Check ductwork for problems in affected areas"
    ],
    
    proTip: "Take temperature readings during the hottest part of a summer day. A photo of a thermometer showing 82°F in a room when the thermostat is set to 72°F is powerful evidence of system inadequacy."
  },

  high_utility_bills: {
    definition: "Significantly increased electric bills, especially during summer months, often indicate HVAC system inefficiency, ductwork problems, or a system working harder than it should to maintain temperature.",
    
    whyItMatters: "Texas electricity costs are already high, and a failing HVAC system can increase summer bills by 30-50% or more. Higher operating costs reduce your home's effective value - it costs more to live there than comparable homes with efficient systems.",
    
    howToCheck: [
      "Compare current bills to the same months last year",
      "Look for trends over 2-3 years",
      "Check if usage (kWh) increased or just rates",
      "Compare to neighbors with similar size homes",
      "Note any correlation with HVAC running time"
    ],
    
    whatToLookFor: [
      "Summer bills 20%+ higher than previous years",
      "Usage increases without lifestyle changes",
      "Bills higher than neighbors with similar homes",
      "Dramatic spikes during heat waves",
      "System running constantly to maintain temperature"
    ],
    
    documentationTips: [
      "Gather 2+ years of electric bills for comparison",
      "Highlight summer months specifically",
      "Calculate percentage increase year-over-year",
      "Note if rates changed vs. usage changed",
      "Document any lifestyle factors that didn't change"
    ],
    
    proTip: "Many utility providers offer usage comparison tools online. Print reports showing your usage vs. similar homes in your area - if you're 20%+ higher, that supports an inefficiency claim."
  },

  struggles_over_100: {
    definition: "If your AC system struggles to maintain temperature when outdoor temps exceed 100°F, it may be undersized for your home, low on refrigerant, have a failing compressor, or be affected by ductwork problems.",
    
    whyItMatters: "Texas regularly sees 20-40+ days per year above 100°F. An AC system that can't handle these temperatures means your home is uncomfortable for significant portions of summer - a clear quality-of-life and value impact.",
    
    howToCheck: [
      "Monitor indoor temp during 100°F+ days",
      "Note if the system runs continuously without reaching set point",
      "Check if the gap between set temp and actual temp increases with outdoor heat",
      "Listen for the system struggling (longer cycles, unusual sounds)"
    ],
    
    whatToLookFor: [
      "Indoor temp 5°F+ above thermostat setting",
      "System running non-stop during peak heat",
      "Temperature climbing during afternoon hours",
      "System never reaching set temperature",
      "Ice forming on refrigerant lines (indicates problems)"
    ],
    
    documentationTips: [
      "Record indoor temps during 100°F+ days",
      "Photograph thermostat showing the gap",
      "Note outdoor temperature at the time",
      "Document how long system ran continuously",
      "Get HVAC assessment of system capacity"
    ],
    
    proTip: "Texas building codes assume AC systems should maintain 75°F when it's 99°F outside. If your system can't do this, it's undersized or failing - both represent value reductions worth documenting."
  },

  strange_noises: {
    definition: "Unusual HVAC noises include grinding, squealing, banging, clicking, or rattling sounds that weren't present when the system was new. These sounds typically indicate mechanical problems with motors, fans, compressors, or ductwork.",
    
    whyItMatters: "Strange noises usually precede component failure. They indicate the system is wearing out and will need repair or replacement. Documenting these sounds helps establish that your HVAC system is not in good working condition.",
    
    howToCheck: [
      "Listen to the system during startup and shutdown",
      "Listen at the outdoor unit during operation",
      "Check the air handler/furnace during a cycle",
      "Note if sounds are constant or intermittent",
      "Identify where sounds are coming from"
    ],
    
    whatToLookFor: [
      "Grinding (motor bearings wearing out)",
      "Squealing (belt problems or motor issues)",
      "Banging (loose or broken components)",
      "Clicking (electrical problems or relay issues)",
      "Rattling (loose ductwork or debris)"
    ],
    
    documentationTips: [
      "Video the system running with audible sounds",
      "Note when sounds occur (startup, shutdown, constantly)",
      "Describe the type and location of sounds",
      "Have an HVAC technician diagnose the cause",
      "Get written estimate for repairs"
    ],
    
    proTip: "Video evidence of unusual HVAC sounds is very effective. The appraisal board can hear what you hear. Make sure to capture the sound clearly - do it when the house is otherwise quiet."
  },

  poor_airflow: {
    definition: "Weak airflow from vents indicates the system isn't moving air effectively. Causes include blower motor problems, clogged filters, ductwork leaks, blockages, or undersized ductwork for the home's cooling needs.",
    
    whyItMatters: "In Texas, you need strong airflow to move enough cooled air to combat 100°F+ heat. Poor airflow means the AC runs constantly but can't cool effectively, increasing wear and energy costs while reducing comfort.",
    
    howToCheck: [
      "Feel airflow at vents throughout the house",
      "Hold a tissue near vents to see movement",
      "Compare airflow between rooms",
      "Check if filters are clean",
      "Listen for whistling that might indicate blockages"
    ],
    
    whatToLookFor: [
      "Noticeably weaker airflow than when system was newer",
      "Some vents with very weak or no airflow",
      "Uneven airflow throughout the house",
      "Reduced airflow even with new filters",
      "Whistling or hissing from ducts"
    ],
    
    documentationTips: [
      "Note which vents have weak airflow",
      "Document filter change schedule",
      "Get blower motor and ductwork assessed",
      "Have HVAC company check system static pressure",
      "Get estimate for repairs"
    ],
    
    proTip: "If airflow is dramatically weaker than it used to be, the blower motor may be failing. This is a several-hundred-dollar repair that indicates the system is aging out."
  },

  frequent_cycling: {
    definition: "Short cycling is when your AC turns on and off every few minutes instead of running for 10-20 minute cycles. This rapid cycling indicates the system is oversized, has a failing component, or has refrigerant problems.",
    
    whyItMatters: "Short cycling dramatically increases wear on your compressor - the most expensive component. Each startup puts significant stress on the system. Frequent cycling also indicates the system isn't operating efficiently.",
    
    howToCheck: [
      "Time how long the system runs before shutting off",
      "Normal cycles are 10-20 minutes",
      "Count how many times it cycles per hour",
      "Note if cycling is worse in certain conditions",
      "Listen for frequent compressor starts"
    ],
    
    whatToLookFor: [
      "Cycles shorter than 10 minutes",
      "System starting and stopping more than 4-6 times per hour",
      "Compressor running very briefly then stopping",
      "Indoor temperature swings between cycles",
      "Higher than expected electricity usage"
    ],
    
    documentationTips: [
      "Time several cycles and record the lengths",
      "Note outdoor temperature during testing",
      "Have HVAC technician check refrigerant levels",
      "Get compressor and system diagnostics",
      "Document if this is a change from past behavior"
    ],
    
    proTip: "Video your thermostat for 30 minutes showing the system cycling on and off rapidly. This visual evidence clearly demonstrates the problem to anyone reviewing your appeal."
  },

  humidity_issues: {
    definition: "Indoor humidity problems - either too humid (sticky, muggy feeling) or too dry - indicate your HVAC system isn't properly conditioning the air. In Texas, excess humidity during summer is the more common problem.",
    
    whyItMatters: "Proper humidity control is a key function of AC systems. If your home is still humid when the AC runs, the system isn't removing moisture effectively. High indoor humidity promotes mold growth and reduces comfort despite low temperatures.",
    
    howToCheck: [
      "Use a humidity meter (hygrometer) - available at hardware stores",
      "Check if windows fog on the inside",
      "Notice if the house feels clammy even when cooled",
      "Look for mold growth in bathrooms or closets",
      "Ideal indoor humidity is 30-50%"
    ],
    
    whatToLookFor: [
      "Indoor humidity above 50-55% when AC is running",
      "Condensation on windows",
      "Musty smells or visible mold",
      "Clammy feeling despite cool temperatures",
      "Wood floors or furniture showing moisture issues"
    ],
    
    documentationTips: [
      "Photograph humidity meter readings",
      "Document conditions when readings were taken",
      "Note any mold or moisture issues",
      "Have HVAC company check system operation",
      "Get assessment of why humidity isn't being controlled"
    ],
    
    proTip: "High indoor humidity can promote mold growth - a health and value issue. If you've had to deal with mold despite running your AC, document this as evidence of HVAC system inadequacy."
  },

  ductwork_issues: {
    definition: "Ductwork problems include disconnected sections, holes, poor sealing, crushed ducts, or deteriorated insulation - usually in the attic where Texas heat amplifies the impact. Duct leaks can waste 20-30% of your cooled air.",
    
    whyItMatters: "In Texas attics reaching 150°F+, duct problems are devastating to efficiency. Cooled air leaking into the attic is immediately lost, while hot attic air gets sucked into leaky return ducts. This dramatically increases cooling costs and system wear.",
    
    howToCheck: [
      "If accessible, visually inspect ductwork in attic",
      "Look for disconnected or hanging sections",
      "Feel for air escaping at duct connections",
      "Look for deteriorated insulation on ducts",
      "Check for crushed or kinked flexible ducts"
    ],
    
    whatToLookFor: [
      "Disconnected duct sections",
      "Visible holes or tears in ductwork",
      "Deteriorated or missing duct insulation",
      "Crushed or kinked flexible ducts",
      "Connections that have pulled apart"
    ],
    
    documentationTips: [
      "If safe, photograph duct problems",
      "Note locations of issues",
      "Have HVAC company do a duct inspection",
      "Get estimate for duct sealing/repair",
      "Document impact on energy bills"
    ],
    
    safetyWarning: "Texas attics are dangerously hot in summer - heat stroke can occur in minutes. Never enter an attic during the heat of the day. If you must inspect, go early morning and limit time to a few minutes.",
    
    proTip: "Many HVAC companies offer duct testing that measures how much air is being lost. A test showing 25%+ duct leakage is compelling evidence of system problems."
  },

  professional_opinion_hvac: {
    definition: "A professional HVAC assessment includes system inspection, diagnostic testing, and recommendations from a licensed technician. This evaluation documents system condition, identifies problems, and provides repair/replacement cost estimates.",
    
    whyItMatters: "Professional documentation carries significant weight with appraisal boards. HVAC technicians can identify problems not visible to homeowners and provide estimates that directly support value adjustments in your appeal.",
    
    howToCheck: [
      "Schedule a maintenance visit and ask for written assessment",
      "Request documentation of any problems found",
      "Get repair estimates for identified issues",
      "Ask about expected remaining system life",
      "Get 2-3 opinions for major recommendations"
    ],
    
    whatToLookFor: [
      "Written assessment on company letterhead",
      "Specific problems identified",
      "Repair vs. replacement recommendations",
      "Cost estimates for recommended work",
      "System age and expected remaining life assessment"
    ],
    
    documentationTips: [
      "Get written assessments from 1-2 companies",
      "Keep all repair estimates",
      "Document technician's license/company credentials",
      "Note any urgency indicated",
      "Ask for system efficiency evaluation"
    ],
    
    proTip: "Many HVAC companies offer free or low-cost tune-ups hoping to find issues. Take advantage of this - a written report recommending replacement is powerful evidence for your appeal."
  },

  // ============================================
  // PLUMBING CHECKLIST ITEMS
  // ============================================

  pipe_age: {
    definition: "Pipe age refers to how long your home's supply and drain pipes have been in service. Different materials have different lifespans: galvanized steel (30-50 years), copper (50+ years), PVC drain lines (25-40 years), and the problematic polybutylene (prone to failure regardless of age).",
    
    whyItMatters: "Texas hard water accelerates pipe deterioration, especially in galvanized steel pipes. Older pipes corrode internally, restrict water flow, and can fail suddenly. Homes with original pipes from the 1970s-1990s often need full or partial repiping.",
    
    howToCheck: [
      "Look at exposed pipes under sinks, in garage, or utility areas",
      "Note the material: copper is reddish-brown, galvanized is silver-gray, PVC is white plastic",
      "Check home records for any repiping work",
      "Look at the water heater connections",
      "Check exposed pipes in the attic if accessible"
    ],
    
    whatToLookFor: [
      "Galvanized steel pipes (dull gray, magnetic) indicate older plumbing",
      "Corrosion buildup on galvanized pipes",
      "Mix of materials suggesting partial repairs",
      "Polybutylene (gray plastic) pipes",
      "Original pipes in homes built before 1990"
    ],
    
    documentationTips: [
      "Photograph exposed pipes showing material type",
      "Note the year your home was built",
      "Document any partial repiping that's been done",
      "Get a plumber's assessment of remaining pipe life",
      "Keep any previous plumbing inspection reports"
    ],
    
    proTip: "If you have galvanized steel pipes and low water pressure, the pipes are corroding closed internally. A plumber can show you a cross-section of a removed galvanized pipe - the buildup is dramatic visual evidence."
  },

  low_water_pressure: {
    definition: "Low water pressure throughout the home indicates systemic plumbing problems - typically corroded galvanized pipes, a failing pressure regulator, municipal supply issues, or a main line restriction. Isolated weak fixtures are usually local issues.",
    
    whyItMatters: "Low water pressure throughout the home is a significant quality-of-life issue affecting everything from showers to washing machines. It typically indicates aging pipes that need replacement - a major expense that should be reflected in property value.",
    
    howToCheck: [
      "Run water at multiple fixtures simultaneously",
      "Check water pressure at an outdoor hose bib with a gauge",
      "Note if pressure is weak everywhere or just certain areas",
      "Check if pressure is worse during peak usage times",
      "Normal pressure is 40-60 PSI"
    ],
    
    whatToLookFor: [
      "Pressure below 40 PSI throughout the home",
      "Pressure dropping significantly when multiple fixtures run",
      "Slow fixture fill times",
      "Weak shower pressure",
      "Appliances taking longer to fill"
    ],
    
    documentationTips: [
      "Buy a pressure gauge ($10-15 at hardware stores)",
      "Test and photograph pressure readings",
      "Note if pressure is worse at certain times",
      "Have a plumber diagnose the cause",
      "Get estimate for repairs/repiping"
    ],
    
    proTip: "A pressure gauge photo showing 25-30 PSI is compelling evidence. Compare to the 40-60 PSI normal range. If the cause is corroded pipes, get photos of the corrosion inside pipe sections."
  },

  slow_drains: {
    definition: "Multiple slow drains throughout the home typically indicate main line problems - roots in the sewer line, a partially collapsed pipe, or buildup in the main drain. Single slow drains are usually local clogs.",
    
    whyItMatters: "Systemic drain issues indicate problems with the main sewer line, which is expensive to repair or replace. In Texas, tree roots commonly invade clay sewer lines, and shifting foundation can crack or misalign pipes.",
    
    howToCheck: [
      "Test drain speed in multiple fixtures",
      "Run the dishwasher or washing machine and check other drains",
      "Note if multiple fixtures drain slowly simultaneously",
      "Check if toilets flush slowly or incompletely",
      "Notice any gurgling sounds from drains"
    ],
    
    whatToLookFor: [
      "Multiple fixtures draining slowly",
      "Backup when appliances drain",
      "Gurgling sounds from drains",
      "Water rising in shower when toilet flushes",
      "Slow flushing toilets"
    ],
    
    documentationTips: [
      "Video slow drains in multiple locations",
      "Document any backup events",
      "Note if problems are worsening over time",
      "Get a sewer line camera inspection",
      "Keep camera inspection video as evidence"
    ],
    
    proTip: "A sewer camera inspection video showing root intrusion or pipe damage is excellent evidence. Many plumbers will provide the video file. This is compelling visual proof of a major issue."
  },

  water_discoloration: {
    definition: "Discolored water - rusty, brown, yellow, or cloudy - coming from faucets indicates pipe corrosion, sediment buildup, or water heater problems. Rust-colored water typically comes from corroding galvanized steel pipes.",
    
    whyItMatters: "Discolored water is visible evidence of pipe deterioration. Rusty water from galvanized pipes means those pipes are corroding from the inside and will eventually fail. This is a health concern and a property value issue.",
    
    howToCheck: [
      "Run cold water and hot water separately",
      "Collect water in a white container to see color clearly",
      "Check if discoloration is worse in the morning (sitting in pipes)",
      "Note if it clears after running water",
      "Check if hot water is more discolored than cold"
    ],
    
    whatToLookFor: [
      "Rusty or brown-tinged water",
      "Sediment or particles in water",
      "Discoloration that's worse in the morning",
      "Hot water more discolored than cold (water heater issue)",
      "Water that doesn't clear after running"
    ],
    
    documentationTips: [
      "Photograph discolored water in white containers",
      "Document when discoloration is worst",
      "Note if it's hot water, cold water, or both",
      "Get water quality testing if concerned",
      "Have a plumber identify the source"
    ],
    
    proTip: "Fill a white container and photograph the discolored water next to a glass of clear bottled water for comparison. This side-by-side comparison is very effective evidence."
  },

  visible_leaks: {
    definition: "Visible plumbing leaks include dripping faucets, water under sinks, wet spots around toilets, or any visible water escape from pipes or fixtures. Even small leaks indicate wear and can lead to larger failures.",
    
    whyItMatters: "Visible leaks are direct evidence of plumbing problems. They waste water, cause damage over time, and indicate aging components that need repair. Multiple leaks throughout a home suggest systemic plumbing issues.",
    
    howToCheck: [
      "Inspect under all sinks for moisture or drips",
      "Check around toilet bases for water",
      "Look at exposed pipe connections",
      "Check water heater area for moisture",
      "Look for water stains on ceilings below bathrooms"
    ],
    
    whatToLookFor: [
      "Dripping faucets or showerheads",
      "Water pooling under sinks",
      "Moisture around toilet bases",
      "Water stains or damage from ongoing leaks",
      "Corrosion or mineral buildup at connections"
    ],
    
    documentationTips: [
      "Photograph all visible leaks",
      "Document water damage from leaks",
      "Note how long leaks have been present",
      "Get plumber quotes to fix all leaks",
      "Document if the same leaks recur after repair"
    ],
    
    proTip: "Multiple small leaks throughout a home often indicate the plumbing system is aging out. Document each one - the total repair cost of fixing everything adds up to significant depreciation."
  },

  water_heater_issues: {
    definition: "Water heater issues include age over 8-10 years, visible rust, inadequate hot water, strange noises, or leaking. Tank water heaters in Texas typically last 8-12 years due to hard water sediment buildup.",
    
    whyItMatters: "Texas hard water dramatically shortens water heater life by accelerating sediment buildup. A failing water heater will eventually leak, potentially causing significant water damage. Replacement costs $1,000-3,000+ for standard tanks.",
    
    howToCheck: [
      "Check the data plate for manufacture date",
      "Look for rust around the tank or on top",
      "Listen for rumbling or popping sounds when heating",
      "Check for any water pooling around the base",
      "Note if hot water supply is decreasing"
    ],
    
    whatToLookFor: [
      "Tank age over 8-10 years",
      "Visible rust on tank exterior",
      "Water around the base (early failure sign)",
      "Rumbling sounds (sediment buildup)",
      "Decreasing hot water capacity"
    ],
    
    documentationTips: [
      "Photograph the data plate showing age",
      "Document any rust or corrosion",
      "Note any performance problems",
      "Get replacement quotes from plumbers",
      "Keep records of any repairs"
    ],
    
    proTip: "A water heater over 10 years old in Texas is living on borrowed time. If you can hear rumbling sounds when it heats, that's sediment boiling at the bottom - the tank is likely failing internally even if it looks okay outside."
  },

  slab_leak: {
    definition: "A slab leak is a leak in the water pipes running under or through your concrete foundation. Signs include unexplained high water bills, warm spots on floors, sounds of running water when nothing is on, or foundation movement from the moisture.",
    
    whyItMatters: "Slab leaks are extremely common in Texas due to foundation movement and copper pipe stress. They're expensive to repair ($2,000-10,000+) and can cause significant foundation damage if left unaddressed. This is a major value-affecting issue.",
    
    howToCheck: [
      "Monitor your water bill for unexplained increases",
      "Check your water meter with all fixtures off - movement indicates a leak",
      "Feel for warm spots on slab floors (hot water leak)",
      "Listen for running water sounds when nothing is running",
      "Look for unexplained wet spots or mold on floors"
    ],
    
    whatToLookFor: [
      "Water bill higher than usage explains",
      "Water meter spinning with all fixtures off",
      "Warm or hot spots on floors",
      "Sound of running water with nothing on",
      "Damp carpet or flooring with no visible source",
      "New foundation cracks appearing"
    ],
    
    documentationTips: [
      "Document water bills showing unexplained increase",
      "Video the water meter spinning with all water off",
      "Get leak detection company to locate the leak",
      "Keep detection company's report and invoice",
      "Get repair estimates from plumbers"
    ],
    
    proTip: "Video your water meter for 60 seconds with all water turned off in the house. If the meter moves at all, you have a leak somewhere. This simple test is compelling evidence of a hidden leak."
  },

  sewer_issues: {
    definition: "Sewer problems include odors in the home, slow drains throughout, backups, or sewage smells outside. These indicate damaged sewer lines, root intrusion, or improperly vented drain systems.",
    
    whyItMatters: "Texas clay soil movement is hard on sewer lines, and tree roots seek out the moisture in sewer pipes. Sewer line replacement is one of the most expensive plumbing repairs, often $5,000-15,000+. This significantly impacts property value.",
    
    howToCheck: [
      "Note any sewage odors inside or outside",
      "Check if multiple drains are slow or backing up",
      "Look for wet spots in the yard over the sewer line",
      "Note any unusual lush grass along the sewer line path",
      "Listen for gurgling sounds when toilets flush"
    ],
    
    whatToLookFor: [
      "Sewage odors in bathrooms or yard",
      "Multiple drain backups",
      "Gurgling sounds from drains",
      "Wet spots in yard over sewer line",
      "Unusually green grass over sewer line (fertilized by leak)"
    ],
    
    documentationTips: [
      "Document odors and when they occur",
      "Note any backup events",
      "Get a sewer camera inspection",
      "Keep the camera inspection video",
      "Get repair/replacement estimates"
    ],
    
    proTip: "A sewer camera inspection costs $200-400 but provides video evidence of any problems. This video showing roots, cracks, or collapse is powerful documentation for your appeal."
  },

  polybutylene_pipes: {
    definition: "Polybutylene (PB) is a gray plastic pipe material used extensively in Texas homes built between 1978-1995. It was marketed as 'the pipe of the future' but has proven prone to sudden, catastrophic failure due to degradation from chlorine in water supplies.",
    
    whyItMatters: "Homes with polybutylene pipes face significant value impacts: many insurance companies won't cover them or charge higher premiums, they can fail without warning causing major water damage, and replacement costs $5,000-15,000+. This is a legitimate and significant depreciation factor.",
    
    howToCheck: [
      "Look under sinks at the supply lines coming from the wall",
      "Check at the water heater inlet/outlet connections",
      "Look at the main water shutoff valve area",
      "Check exposed pipes in garage, crawl space, or utility areas",
      "Look for stamping: 'PB2110' is the telltale code"
    ],
    
    whatToLookFor: [
      "Gray plastic pipes (not white PVC or copper)",
      "Flexible plastic pipes with copper crimp rings at connections",
      "Stamping showing 'PB' or 'PB2110' on pipe surface",
      "Note: Interior pipes may be gray while main line is blue",
      "Connections with copper or aluminum bands"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/polybutylene-pipe.jpg", 
        alt: "Gray polybutylene pipe", 
        caption: "Gray polybutylene pipe with PB2110 stamping and copper crimp rings - these pipes are prone to failure" 
      }
    ],
    
    documentationTips: [
      "Photograph the gray pipes with visible stamping",
      "Get a close-up of PB2110 marking if visible",
      "Note all locations where PB pipes are found",
      "If insurance declined coverage or charged more, document that",
      "Get repiping estimates from plumbers"
    ],
    
    safetyWarning: "Do not attempt to replace polybutylene pipes yourself. The connections require special tools and improper repair can cause leaks. Also, some PB pipes are under concrete and require professional assessment.",
    
    proTip: "An insurance declination letter or policy showing polybutylene exclusion is powerful evidence. It proves that professionals consider these pipes a significant risk requiring costly replacement."
  },

  freeze_damage: {
    definition: "Freeze damage refers to pipes that burst, cracked, or were weakened during freezing events. The February 2021 Texas winter storm caused unprecedented pipe damage across the state when temperatures stayed below freezing for days.",
    
    whyItMatters: "The 2021 freeze damaged plumbing in millions of Texas homes. Even pipes that didn't burst may have been weakened by expansion. Many homes still have unrepaired damage or damage that was poorly patched. This represents ongoing value reduction.",
    
    howToCheck: [
      "Recall if your home had any pipe issues during the 2021 freeze",
      "Look for patched or repaired pipe sections",
      "Check for reduced water pressure since the freeze",
      "Look in attic for any repaired sections",
      "Review any insurance claims from that period"
    ],
    
    whatToLookFor: [
      "Patched pipe sections",
      "New pipe materials mixed with old",
      "Reduced water pressure since the freeze",
      "Ongoing slow leaks that started after freeze",
      "Insurance claims or repair invoices from 2021"
    ],
    
    documentationTips: [
      "Document any freeze-related repairs",
      "Keep insurance claim records",
      "Note any ongoing issues since the freeze",
      "Photograph any visible repair patches",
      "Get plumber assessment of pipe condition"
    ],
    
    proTip: "The 2021 freeze is well-documented. If you had damage, reference this event specifically - appraisal boards are very familiar with the widespread impact and understand the ongoing implications."
  },

  professional_opinion_plumbing: {
    definition: "A professional plumbing assessment includes inspection of visible pipes, fixtures, water heater, and drain function by a licensed plumber. They can also perform camera inspections of drain lines and leak detection for hidden issues.",
    
    whyItMatters: "Professional plumbing assessments document problems that affect value. Plumbers can identify issues homeowners miss and provide repair estimates that directly support value adjustments. Camera inspections provide visual evidence of hidden problems.",
    
    howToCheck: [
      "Schedule an inspection with a licensed plumber",
      "Request a written assessment of system condition",
      "Ask for camera inspection of drain/sewer lines",
      "Get estimates for any recommended repairs",
      "Ask about remaining life of plumbing components"
    ],
    
    whatToLookFor: [
      "Written assessment on company letterhead",
      "Specific problems identified",
      "Camera inspection video if performed",
      "Repair vs. replacement recommendations",
      "Cost estimates for recommended work"
    ],
    
    documentationTips: [
      "Get written assessments from qualified plumbers",
      "Keep all repair estimates",
      "Request camera inspection video files",
      "Document any urgency in recommendations",
      "Keep any photos taken during inspection"
    ],
    
    proTip: "Sewer camera inspections are relatively inexpensive ($200-400) but provide dramatic video evidence of any problems. This visual documentation is extremely compelling for your appeal."
  },

  // ============================================
  // ELECTRICAL CHECKLIST ITEMS
  // ============================================

  panel_age: {
    definition: "Electrical panel age is measured from original installation. Panels over 25 years old may have outdated components, insufficient capacity for modern usage, and may not meet current safety codes even if still functioning.",
    
    whyItMatters: "Older Texas homes often have electrical panels designed for much lower power usage than modern households with AC systems, multiple TVs, computers, and appliances. An undersized or outdated panel limits home functionality and may not be insurable.",
    
    howToCheck: [
      "Look for a date on the panel or main breaker",
      "Check home records for electrical upgrades",
      "Count the number of breaker spaces",
      "Look for the amperage rating (100A, 150A, 200A)",
      "Check for signs of age like rust or discoloration"
    ],
    
    whatToLookFor: [
      "Panel manufactured before 2000",
      "100 amp or lower service",
      "Limited breaker spaces (full panel)",
      "Rust, corrosion, or discoloration",
      "Outdated breaker types"
    ],
    
    documentationTips: [
      "Photograph the panel data plate",
      "Document the amperage rating",
      "Note how many breaker spaces are used",
      "Get an electrician's assessment",
      "Get upgrade estimates if recommended"
    ],
    
    proTip: "If your panel is full (no empty breaker spaces), any electrical additions to your home will require an expensive panel upgrade. This limitation affects home value."
  },

  fuse_box: {
    definition: "A fuse box uses screw-in glass fuses instead of circuit breakers. Fuses must be replaced when they blow, unlike breakers which simply flip off and reset. Fuse boxes are a clear sign of significantly outdated electrical systems.",
    
    whyItMatters: "Fuse boxes indicate electrical systems from the 1960s or earlier. They're considered outdated safety technology, often insufficient for modern power needs, and may make a home uninsurable or difficult to insure. Replacement costs $1,500-4,000+.",
    
    howToCheck: [
      "Look at your electrical panel",
      "Fuse boxes have round glass or ceramic screw-in fuses",
      "Breaker panels have rectangular switches that flip",
      "Fuse boxes are typically smaller than modern panels",
      "May be located in unusual places (older home layouts)"
    ],
    
    whatToLookFor: [
      "Round screw-in fuses instead of switches",
      "Glass fuses with visible filaments",
      "Small panel size (4-8 fuse positions typical)",
      "60 amp or lower total service",
      "Pennies or other improper 'repairs' behind fuses"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/fuse-box-example.jpg", 
        alt: "Old fuse box", 
        caption: "Fuse boxes use screw-in glass fuses - a clear indicator of very outdated electrical service" 
      },
      { 
        src: "/images/maintenance/breaker-panel.jpg", 
        alt: "Modern breaker panel", 
        caption: "Modern breaker panels use flip switches and provide more capacity and safety features" 
      }
    ],
    
    documentationTips: [
      "Photograph the fuse box clearly",
      "Note the amperage rating",
      "Document if any insurance issues have arisen",
      "Get electrician quotes for panel upgrade",
      "Note any known electrical problems"
    ],
    
    safetyWarning: "Never put pennies or foil behind fuses to 'fix' them - this defeats the safety protection and creates fire risk. If you find evidence of this, note it as a serious safety issue.",
    
    proTip: "Insurance companies often won't insure homes with fuse boxes, or charge significant premiums. A letter from an insurer requiring an upgrade is powerful documentation for your appeal."
  },

  panel_brand: {
    definition: "Federal Pacific Electric (FPE), Zinsco, and Pushmatic are electrical panel brands with documented safety defects. Federal Pacific Stab-Lok breakers have a failure rate of up to 60% - meaning they may not trip when they should, creating fire risk.",
    
    whyItMatters: "These panels are considered hazardous and often require replacement to obtain homeowner's insurance or sell the home. The Consumer Product Safety Commission has investigated FPE panels. Replacement costs $1,500-4,000+, a clear value reduction.",
    
    howToCheck: [
      "Open your electrical panel door (outer cover only - don't remove inner cover)",
      "Look for brand name on the panel door or inside edge",
      "Federal Pacific: Look for 'FPE' or 'Stab-Lok' branding",
      "Zinsco: May say 'Zinsco', 'GTE-Sylvania', or 'Kearney'",
      "Pushmatic: Has distinctive push-button breakers"
    ],
    
    whatToLookFor: [
      "Federal Pacific: Orange/red colored breakers, 'Stab-Lok' label",
      "Zinsco: Colored breaker handles (pink, blue, green)",
      "Pushmatic: Round push buttons instead of toggle switches",
      "Any panel over 40 years old warrants professional inspection"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/federal-pacific-label.jpg", 
        alt: "Federal Pacific panel", 
        caption: "Federal Pacific Stab-Lok panel - note the distinctive orange breakers and FPE label" 
      },
      { 
        src: "/images/maintenance/zinsco-panel.jpg", 
        alt: "Zinsco panel", 
        caption: "Zinsco panel - identifiable by colored breaker handles (pink, blue, green)" 
      }
    ],
    
    documentationTips: [
      "Photograph the panel door showing brand name",
      "Get a photo of the breakers (without removing cover)",
      "If an electrician has recommended replacement, include their quote",
      "Document any insurance requirements or declinations",
      "Note the documented safety issues with these brands"
    ],
    
    safetyWarning: "Never remove the inner panel cover - exposed bus bars carry lethal current. Only open the outer door. If you smell burning or see scorch marks, contact an electrician immediately.",
    
    proTip: "Research the Consumer Product Safety Commission findings on Federal Pacific panels. Including this documentation with your appeal shows the recognized safety issues that require costly panel replacement."
  },

  frequent_trips: {
    definition: "Circuit breakers that trip (shut off) frequently indicate overloaded circuits, short circuits, or failing breakers. Occasional trips during high-demand activities may be normal, but frequent trips indicate electrical system problems.",
    
    whyItMatters: "In Texas, frequent trips often occur during summer when AC systems stress electrical capacity. This indicates either an undersized electrical system or aging components that can't handle the load. Both represent value-affecting issues.",
    
    howToCheck: [
      "Note which breakers trip and how often",
      "Identify what's running when trips occur",
      "Check if trips happen during high AC usage",
      "Note if the same breakers trip repeatedly",
      "Check if breakers feel hot when they trip"
    ],
    
    whatToLookFor: [
      "Same breakers tripping multiple times per week",
      "Trips when running normal appliances",
      "Trips during AC heavy usage periods",
      "Breakers that won't reset firmly",
      "Warm or hot breakers"
    ],
    
    documentationTips: [
      "Keep a log of which breakers trip and when",
      "Note what appliances were running",
      "Document the frequency over time",
      "Have an electrician evaluate the cause",
      "Get quotes for electrical upgrades if needed"
    ],
    
    safetyWarning: "If a breaker won't reset or feels hot, don't force it. This may indicate a serious problem requiring immediate electrician evaluation.",
    
    proTip: "A breaker that trips frequently on a circuit with normal load is often failing internally. Breaker replacement is inexpensive, but repeated tripping across multiple breakers indicates system-wide capacity issues."
  },

  flickering_lights: {
    definition: "Lights that flicker or dim when the AC starts, or during normal use, indicate electrical system issues - typically undersized wiring, loose connections, or service capacity problems that can't handle momentary high draws.",
    
    whyItMatters: "In Texas, lights dimming when AC kicks on is common in homes with undersized electrical service. It indicates the electrical system is being stressed beyond comfortable capacity - a quality and safety issue that affects value.",
    
    howToCheck: [
      "Watch lights when AC compressor starts",
      "Note any flickering during normal use",
      "Check if specific circuits are worse",
      "See if the problem has gotten worse over time",
      "Check all areas of the house for consistency"
    ],
    
    whatToLookFor: [
      "Momentary dimming when AC starts",
      "Random flickering throughout the house",
      "Specific fixtures that flicker constantly",
      "Dimming when multiple appliances run",
      "Flickering that's gotten worse over time"
    ],
    
    documentationTips: [
      "Video the flickering or dimming",
      "Note what triggers the issue",
      "Document if it's getting worse",
      "Have electrician evaluate causes",
      "Get quotes for any recommended fixes"
    ],
    
    proTip: "Video your lights dimming dramatically when the AC kicks on. This visual evidence clearly demonstrates the electrical system is stressed and may need upgrading."
  },

  two_prong_outlets: {
    definition: "Two-prong (ungrounded) outlets lack the third grounding hole present in modern outlets. They indicate wiring installed before grounding became standard (typically pre-1970s) and represent both a safety issue and code deficiency.",
    
    whyItMatters: "Ungrounded outlets can't safely power modern electronics with three-prong plugs. Using adapters defeats safety protection. Homes with ungrounded outlets may have difficulty with insurance and often require rewiring to sell.",
    
    howToCheck: [
      "Look at outlets throughout your home",
      "Count two-prong vs. three-prong outlets",
      "Check older areas of the home especially",
      "Note if adapters are being used",
      "Check if any three-prong outlets are properly grounded"
    ],
    
    whatToLookFor: [
      "Outlets with only two slots (no round grounding hole)",
      "Three-prong adapters being used on two-prong outlets",
      "Mix of two and three-prong outlets (partial updates)",
      "All two-prong outlets (no updates done)"
    ],
    
    visualExamples: [
      { 
        src: "/images/maintenance/two-prong-outlet.jpg", 
        alt: "Two-prong ungrounded outlet", 
        caption: "Two-prong outlets lack the grounding connection required for safe use of modern electronics" 
      },
      { 
        src: "/images/maintenance/three-prong-outlet.jpg", 
        alt: "Three-prong grounded outlet", 
        caption: "Modern three-prong outlets include a grounding connection for safety" 
      }
    ],
    
    documentationTips: [
      "Count and photograph two-prong outlets",
      "Note what percentage of outlets are ungrounded",
      "Document any adapter use",
      "Get electrician quote for upgrading outlets",
      "Note any insurance concerns raised"
    ],
    
    proTip: "Simply replacing a two-prong outlet with a three-prong outlet doesn't add grounding - the wiring must also be updated. An electrician can test whether your three-prong outlets are actually properly grounded."
  },

  warm_outlets: {
    definition: "Outlets or switches that feel warm or hot to the touch indicate excessive electrical resistance - typically from loose connections, damaged wiring, or overloaded circuits. This is a fire hazard requiring immediate attention.",
    
    whyItMatters: "Warm outlets are a serious safety warning sign. Electrical resistance generates heat, and excess heat in wiring can cause fires. This issue requires professional evaluation and repair, representing both a safety and value concern.",
    
    howToCheck: [
      "Feel outlets and switches throughout your home",
      "Check especially after using the outlet",
      "Pay attention to outlets with heavy loads (AC, appliances)",
      "Check both the outlet face and the wall around it",
      "Test at different times of day"
    ],
    
    whatToLookFor: [
      "Outlets warm to the touch during or after use",
      "Discoloration around outlets (heat damage)",
      "Burning smell near outlets",
      "Outlets that spark when plugging in",
      "Switches that feel warm when off"
    ],
    
    documentationTips: [
      "Note which outlets feel warm",
      "Document when and under what conditions",
      "Have electrician evaluate immediately",
      "Get written assessment of cause",
      "Keep repair estimates and invoices"
    ],
    
    safetyWarning: "Warm outlets are a potential fire hazard. If an outlet is hot (not just warm), smells like burning, or shows discoloration, stop using it immediately and call an electrician. This is not a DIY situation.",
    
    proTip: "If you find a warm outlet, take a photo of an infrared thermometer reading the temperature if you have one. Documented elevated temperatures are compelling evidence of a serious electrical problem."
  },

  aluminum_wiring: {
    definition: "Aluminum wiring was used in many homes built between 1965-1973 as a copper alternative. It has been identified as a fire hazard because aluminum expands and contracts more than copper, loosening connections and creating heat buildup.",
    
    whyItMatters: "Homes with aluminum wiring have a higher fire risk and may be difficult or expensive to insure. Proper remediation involves either replacing the wiring or installing special connectors at every connection point - both are costly.",
    
    howToCheck: [
      "Look at the wires visible in your electrical panel (outer cover only)",
      "Aluminum is silver-colored; copper is reddish-brown",
      "Check exposed wiring in attic or unfinished areas",
      "Look for 'AL' or 'Aluminum' markings on wire insulation",
      "Have an electrician inspect if uncertain"
    ],
    
    whatToLookFor: [
      "Silver-colored wires in panel or outlets",
      "AL or ALUMINUM printed on wire insulation",
      "Home built between 1965-1973",
      "Warm outlets or switches (aluminum connection problems)",
      "Flickering lights (may indicate connection issues)"
    ],
    
    documentationTips: [
      "Photograph any visible aluminum wiring",
      "Note if home was built in the aluminum era (1965-73)",
      "Document any insurance requirements or issues",
      "Get electrician assessment and remediation quote",
      "Keep documentation of any repairs made"
    ],
    
    safetyWarning: "Don't attempt to work on aluminum wiring yourself. It requires special techniques and connectors. Improper handling increases fire risk. Always use a qualified electrician.",
    
    proTip: "If your insurance company requires an electrical inspection due to home age, and aluminum wiring is found, their documentation of this requirement is excellent support for your appeal."
  },

  insufficient_capacity: {
    definition: "Electrical service capacity is measured in amps. Older homes often have 60 or 100 amp service, while modern homes need 150-200 amps. Undersized service means the home can't safely power modern appliances and AC systems.",
    
    whyItMatters: "Texas homes with central AC need adequate electrical capacity. A 100 amp panel can struggle with AC plus modern appliances, computers, and electronics. Upgrading service costs $1,500-4,000+, a direct value reduction.",
    
    howToCheck: [
      "Look at the main breaker in your panel - it shows total amps",
      "Check if panel is full (no empty breaker spaces)",
      "Note if breakers trip during high-demand periods",
      "Count major electrical loads in your home",
      "Check if panel is 60, 100, 150, or 200 amp"
    ],
    
    whatToLookFor: [
      "Main breaker rated at 100 amps or less",
      "Panel with no empty spaces",
      "Frequent breaker trips during summer",
      "Multiple double-tapped breakers (two wires on one breaker)",
      "Extension cords used because outlets are insufficient"
    ],
    
    documentationTips: [
      "Photograph the main breaker showing amperage",
      "Document any capacity-related problems",
      "Note if panel is full or has double-tapped breakers",
      "Get electrician assessment of capacity needs",
      "Get quotes for service upgrade if recommended"
    ],
    
    proTip: "A home with 100 amp service that has central AC, electric range, electric dryer, and modern electronics is likely at its limit. An electrician can calculate your actual electrical load vs. available capacity."
  },

  professional_opinion_electrical: {
    definition: "A professional electrical assessment includes panel evaluation, wiring inspection, capacity analysis, and safety evaluation by a licensed electrician. They can identify code violations, safety hazards, and necessary upgrades.",
    
    whyItMatters: "Professional electrical assessments document problems that affect value and safety. Electricians can identify issues not visible to homeowners and provide repair estimates that directly support value adjustments in your appeal.",
    
    howToCheck: [
      "Schedule an inspection with a licensed electrician",
      "Request a written assessment of system condition",
      "Ask about code compliance and safety issues",
      "Get estimates for any recommended upgrades",
      "Ask about remaining system life expectancy"
    ],
    
    whatToLookFor: [
      "Written assessment on company letterhead",
      "Specific problems and code violations identified",
      "Panel brand and capacity evaluation",
      "Upgrade recommendations with cost estimates",
      "Electrician's license number on documentation"
    ],
    
    documentationTips: [
      "Get written assessments from licensed electricians",
      "Keep all upgrade estimates",
      "Document any safety issues identified",
      "Note urgency of recommendations",
      "Keep before/after documentation if work is done"
    ],
    
    proTip: "If selling your home would require electrical upgrades (many buyers' lenders require safe electrical), document this requirement. The cost to bring electrical to sale-ready condition is a legitimate depreciation factor."
  }
};

/**
 * Get helper content for a specific checklist item
 * @param itemId - The checklist item ID
 * @returns The helper content or undefined if not found
 */
export function getHelperContent(itemId: string): HelperContentData | undefined {
  return HELPER_CONTENT[itemId];
}

/**
 * Check if helper content exists for an item
 * @param itemId - The checklist item ID
 * @returns Whether helper content exists
 */
export function hasHelperContent(itemId: string): boolean {
  return itemId in HELPER_CONTENT;
}

export default HELPER_CONTENT;